// @givmo/board — a director's own conflict-of-interest disclosures.
//
// ══ THE DIRECTOR'S DOOR, AND WHY IT IS A SEPARATE ONE ══════════════════════════════════════════
//
// The same fact — an interest, a date, a counterparty — recorded by two different people. What differs
// is never the content but the PROVENANCE: the officer's door names the seat in the request and carries
// a required note saying where the disclosure came from, because they are writing down somebody else's
// act. This door carries neither, because there is nobody else: the seat is the one this account holds
// on the board, and the route takes no id that could name a colleague.
//
// A director sees ONLY their own. Not by a filter here — this app never asks for anyone else's and the
// backend has no parameter that would let it.
//
// ══ SIGNATURE-CLASS, THROUGH THE SIGNING WINDOW ════════════════════════════════════════════════
//
// Recording your own disclosure is an act performed THROUGH the module, so it runs through the shared
// sheet's gate like a consent signature (`features/director/director-act.tsx`): an open window records
// at once; a shut one brings the sheet up by itself, over the dialog, and the act is sent once more
// after it with the fields exactly as typed — the thunk holds them, and the dialog stays open behind
// the sheet, so nothing typed is lost. The method that answered and the sentence affirmed are
// recorded on the record's own approval event.
//
// ══ WHAT THIS PAGE MUST NOT SAY ════════════════════════════════════════════════════════════════
//
// Nothing here tells a director what their disclosure means, whether they should stand out of a vote,
// or whether anything they have recorded is a problem. Recording a disclosure decides nothing: whether
// a director takes part in a vote is the board's determination, recorded in the minutes as a recusal.
// The page says that once, plainly, and otherwise reports what the book holds.
//
// ══ THE OFFICER PAGE'S NAME AND REGISTER (CBG-UX10, D-CBG-40; frame c6) ═══════════════════════════
//
// "Conflicts of interest", the officer page's name, everywhere "What you’ve told the board" was
// (Jonathan, 2026-10-24 14:40 PDT: "this whole "what you've told the board" label is confusing
// everywhere"). Each of the person's own disclosures is the officer register's entry, with its
// citations: "This record", and the disclosure it replaced or that replaced it, each a link to the
// record itself. "Disclose an interest" stands where the officer has "Record a disclosure".
//
// ══ THE YEAR'S ANNUAL DISCLOSURE (CBG-YR2-WEB2), DARK UNTIL SERVED ══════════════════════════════
//
// Where the backend serves the annual cycle, a card for the year waiting on the person sits at the top
// (`director-year-card.tsx`), and the list is "In the board's records", each annual disclosure as its
// year with the interests its answers name. Where it doesn't (the person's year list is the route
// miss), the page is exactly what it was.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Skeleton,
  TextField,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { GroupLabel } from '../../components/fine-print';
import { RegisteredDisclaimer } from '../../components/registered-disclaimer';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { Link } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import {
  readDirectorDraft,
  useDirectorDraft,
  type DirectorDraftFields,
  type DirectorDraftScope,
} from '../../director/director-drafts';
import { useBoardDirectorHost } from '../../director/director-host';
import { boardDirectorMeKey } from '../../director/director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from '../../director/director-wire';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import {
  disclosureRecordedLine,
  disclosureStatusLabel,
  disclosureStatusTone,
  extractDisclosures,
  type ConflictDisclosureRow,
} from '../conflicts/conflicts-decode';
import { DirectorSetupOfferPage, actMessage, useDirectorAct } from './director-act';
import { DirectorBoardContent } from './director-board-content';
import { DirectorReadFailed, useDirectorFailureWords } from '../../director/access-refusal';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { directorPagesWords as dp } from './director-pages-words';
import { DirectorShell } from './director-shell';
import { DirectorMovePointer } from './move-pointer';
import { coiWords } from '../conflicts/coi-words';
import { hasKeptAnswers, useOwnCoiCycles } from './director-coi';
import { OwnAnnualCard, YearCard, yearForCard } from './director-year-card';

/** The account's own disclosures on this board: one key, which the dialog's act refreshes. */
function disclosuresKey(orgRef: string | null) {
  return ['board-director-disclosures', orgRef] as const;
}

export function DirectorDisclosuresPage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): A director's own disclosures is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board)) return <DirectorBoardOnlyPage testId="page-director-disclosures" />;
  return <DirectorDisclosuresPageForTheBoard />;
}

function DirectorDisclosuresPageForTheBoard() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  // The board the dialog is open for. It belongs to that board: when the address names another, it
  // closes, and nothing typed for one board is shown, kept or sent for another (review-43 D2). Its
  // draft stays where it was, and comes back on that board's page.
  const [recording, setRecording] = useState<string | null>(null);
  if (recording !== null && recording !== orgRef) setRecording(null);

  // A disclosure this account was writing when its session ended (`director-drafts.ts`): once the
  // seat is known, the dialog comes back open with the words as they were typed, once per board.
  const { account } = useBoardDirectorHost();
  const draftScope = useMemo<DirectorDraftScope | null>(
    () =>
      account?.uid !== undefined && orgRef !== null && board?.board_member_id
        ? { uid: account.uid, orgRef, seatId: board.board_member_id, surface: 'disclosure' }
        : null,
    [account?.uid, orgRef, board?.board_member_id],
  );
  const reopenedFor = useRef<DirectorDraftScope | null>(null);
  useEffect(() => {
    if (draftScope === null || reopenedFor.current === draftScope) return;
    reopenedFor.current = draftScope;
    if (readDirectorDraft(draftScope) !== null) setRecording(draftScope.orgRef);
  }, [draftScope]);

  const query = useQuery({
    queryKey: disclosuresKey(orgRef),
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorListOwnBoardConflictDisclosures({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractDisclosures(data);
    },
  });

  const entries = query.data ?? [];
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);

  // CBG-YR2-WEB2: the person's years, `null` while the year is not served here (today's page).
  const cyclesQuery = useOwnCoiCycles(orgRef, mayFire);
  const cycles = cyclesQuery.data ?? null;
  const served = cycles !== null;
  const year = cycles === null ? null : yearForCard(cycles);
  const kept = hasKeptAnswers(
    year !== null && draftScope !== null
      ? { ...draftScope, surface: 'coi-questionnaire', cycleId: year.cycle_id }
      : null,
  );
  const canSign = board?.another_account_required !== true;
  // The organization's name where the board's is missing (approved, CBG-YR2-COPY1).
  const organization = board?.organization_name?.trim() || 'Your organization';

  const list = query.isPending ? (
    <div className="flex flex-col gap-12" data-testid="director-disclosures-loading">
      <Skeleton className="h-48 w-full rounded-lg" />
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  ) : query.isError && isSigningRefusal(query.error) ? null : query.isError ? (
    <DirectorReadFailed
      read={query}
      line="We couldn’t load your disclosures just now."
      testId="director-disclosures-error"
      retryTestId="director-disclosures-retry"
      lineClassName="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
    />
  ) : entries.length === 0 ? (
    <Card testId="director-disclosures-empty">
      <CardBody>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {/* What the board's RECORDS hold from them, in the heading's own words ("In the board's
              records"), not a statement that they have no interests (CBG-UX-COPY3's D7). */}
          Nothing from you is in the board’s records yet.
        </p>
      </CardBody>
    </Card>
  ) : (
    <ul className="flex flex-col gap-12" data-testid="director-disclosures-list">
      {entries.map((entry) => (
        <li key={entry.record_id} data-testid={`director-disclosure-${entry.record_id}`}>
          {entry.annual !== null && orgRef !== null ? (
            <OwnAnnualCard orgRef={orgRef} entry={entry} />
          ) : (
            <OwnDisclosureCard entry={entry} search={search} />
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <DirectorShell
      title={dp.conflictsTitle}
      description="Interests you’ve disclosed to this board, and when you disclosed them."
      identity={directorBoardIdentity(board)}
      actions={
        // CBG-SEC2-L4-MOVE: an account that can't sign here records no disclosure either; the
        // move's pointer below says why.
        orgRef !== null && !notYours && board?.another_account_required !== true ? (
          <Button
            appearance="saas"
            // The year's card holds the page's one primary act while it shows (d1-year-card).
            tone={year !== null ? 'secondary' : 'primary'}
            onClick={() => setRecording(orgRef)}
            testId="director-disclosure-add"
          >
            Disclose an interest
          </Button>
        ) : undefined
      }
      testId="page-director-disclosures"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {/* CBG-SEC2-L4-MOVE (frames b1, b1n): where "Disclose an interest" was, on an account
            another organization's sign-in lock binds. */}
        {orgRef !== null && board?.another_account_required === true ? (
          <Card testId="director-disclosures-move">
            <CardBody>
              <DirectorMovePointer
                orgRef={orgRef}
                blocks="sign"
                testId="director-disclosure-move-pointer"
              />
            </CardBody>
          </Card>
        ) : null}
        {year !== null && orgRef !== null ? (
          <YearCard
            orgRef={orgRef}
            cycle={year}
            organization={year.organization_name?.trim() || organization}
            kept={kept}
            canSign={canSign}
          />
        ) : null}

        <RegisteredDisclaimer
          surface="BOARD_SELF_REPORTED"
          orgRef={orgRef}
          testId="director-disclosures-registered-disclaimer"
        />

        <DirectorReadRefusal reads={[query]} testId="director-disclosures-read-step-up" />

        {served ? (
          // CBG-YR2-WEB2 (d1-year-card): the list under its label, as one section.
          <section className="flex flex-col gap-10" data-testid="director-coi-records">
            <GroupLabel>{coiWords.inBoardsRecords}</GroupLabel>
            {list}
          </section>
        ) : (
          list
        )}

        {recording !== null && recording === orgRef ? (
          <DiscloseDialog
            key={orgRef}
            orgRef={orgRef}
            draftScope={draftScope}
            identity={directorBoardIdentity(board)}
            onClose={() => setRecording(null)}
            onRecorded={() => setRecording(null)}
          />
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

/**
 * One of the person's own disclosures: the officer register's entry (`conflicts-page.tsx`'s
 * `RegisterEntry`) without the name, which is theirs. Each citation is a link to the record itself,
 * never a claim about it.
 */
function OwnDisclosureCard({
  entry,
  search,
}: {
  entry: ConflictDisclosureRow;
  search: Record<string, string> | undefined;
}) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-center gap-8">
          <Badge tone={disclosureStatusTone(entry.status)}>
            {disclosureStatusLabel(entry.status)}
          </Badge>
          {entry.disclosed_on !== null ? (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              disclosed {displayDate(entry.disclosed_on)}
            </span>
          ) : null}
        </div>

        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {entry.title}
        </span>

        {/* Their own words, whitespace preserved: reflowing a disclosure changes what it says. */}
        <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {entry.interest_summary}
        </p>

        {entry.counterparty_name !== null ? (
          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-secondary">
            The other party: {entry.counterparty_name}
          </span>
        ) : null}

        {/* CBG-UX-COPY2 (A8): the backend's sentence about this entry is the officer register's,
            in the third person; the card already shows its date and the other party above. */}

        <div className="flex flex-wrap items-center gap-10">
          <CitationLink
            recordId={entry.record_id}
            label={dp.thisRecord}
            search={search}
            testId={`director-disclosure-cite-self-${entry.record_id}`}
          />
          {entry.supersedes_record_id !== null ? (
            <CitationLink
              recordId={entry.supersedes_record_id}
              label="The disclosure this replaced"
              search={search}
              testId={`director-disclosure-cite-supersedes-${entry.record_id}`}
            />
          ) : null}
          {entry.superseded_by_record_id !== null ? (
            <CitationLink
              recordId={entry.superseded_by_record_id}
              label="The disclosure that replaced this"
              search={search}
              testId={`director-disclosure-cite-superseded-by-${entry.record_id}`}
            />
          ) : null}
        </div>

        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {disclosureRecordedLine(entry.recorded_at, entry.capture_method, 'own')}
        </span>
      </CardBody>
    </Card>
  );
}

/** The officer register's citation (`conflicts-page.tsx`), to the director's record page. */
function CitationLink({
  recordId,
  label,
  search,
  testId,
}: {
  recordId: string;
  label: string;
  search: Record<string, string> | undefined;
  testId: string;
}) {
  return (
    <Link
      to={BOARD_DIRECTOR_PAGES.record}
      params={{ recordId }}
      search={search}
      className="font-sans text-s12 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
      data-testid={testId}
    >
      {label}
    </Link>
  );
}

function DiscloseDialog({
  orgRef,
  draftScope,
  identity,
  onClose,
  onRecorded,
}: {
  orgRef: string;
  /** Where this account's unsent disclosure is kept; null keeps none. */
  draftScope: DirectorDraftScope | null;
  identity: string | undefined;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardDirectorOperations();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const [interest, setInterest] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [disclosedOn, setDisclosedOn] = useState(todayAsDateInput());
  const [error, setError] = useState<string | null>(null);
  // The director's own words, as they left them (a draft kept across a forced sign-out), put back
  // once the seat is known, which can be after the dialog opened (`/me` still answering), and only
  // into a dialog nothing has been typed in yet (review-43 D1).
  const untouched =
    title === '' && interest === '' && counterparty === '' && disclosedOn === todayAsDateInput();
  const restore = (kept: DirectorDraftFields) => {
    if (!untouched) return;
    setTitle(kept.title ?? '');
    setInterest(kept.interest ?? '');
    setCounterparty(kept.counterparty ?? '');
    if (kept.disclosedOn !== undefined) setDisclosedOn(kept.disclosedOn);
  };
  // Only what the director typed: the date is theirs only once they changed it from today.
  const draftWriter = useDirectorDraft(
    draftScope,
    {
      title,
      interest,
      counterparty,
      disclosedOn: disclosedOn === todayAsDateInput() ? '' : disclosedOn,
    },
    restore,
  );

  /**
   * Put the words away: the director closed the dialog, by Cancel, Escape or a click outside. Only
   * an ending they didn't choose (a forced sign-out) keeps them (`director-drafts.ts`).
   */
  const cancel = () => {
    draftWriter.discard();
    onClose();
  };

  const record = useDirectorAct('conflict_disclosure', {
    onDone: async () => {
      draftWriter.discard();
      toast.success('Recorded. Your board’s secretary can see it.');
      await queryClient.invalidateQueries({ queryKey: disclosuresKey(orgRef) });
      onRecorded();
    },
    onFailed: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
        setError('The words above changed. Read them, then try again.');
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        return;
      }
      setError(failureWords(caught, 'We couldn’t save that. Please try again.'));
    },
  });

  const hasDate = disclosedOn.trim().length > 0;
  const ready = interest.trim().length > 0 && hasDate && !record.pending && record.intent !== null;

  const begin = () => {
    setError(null);
    // The thunk holds the fields as typed: a confirm-and-retry sends exactly these.
    const body = {
      interest_summary: interest.trim(),
      title: title.trim() || null,
      counterparty_name: counterparty.trim() || null,
      // Required (CBG-FIX6): a disclosure is filed as the record at once, and a record is made only
      // with its date — the save below stays withdrawn until there is one.
      disclosed_on: disclosedOn.trim(),
    };
    record.begin(
      (intentStatementId) => () =>
        operations.directorRecordOwnBoardConflictDisclosure({
          path: { org_ref: orgRef },
          body: { ...body, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  if (record.offering) {
    return (
      <DirectorSetupOfferPage
        variant="from-sign"
        identity={identity}
        onSettled={record.settleOffer}
      />
    );
  }

  const shownError = actMessage(record, error);

  return (
    <>
      <Dialog.Root open onOpenChange={(next) => !next && !record.pending && cancel()}>
        <Dialog.Content
          aria-label="Disclose an interest"
          className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
          testId="director-disclosure-dialog"
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            Disclose an interest
          </Dialog.Title>

          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {/* The one sentence that keeps a volunteer from reading this as a verdict on themselves. */}
            Describe the interest in your own words. Recording it decides nothing — whether you take
            part in a particular vote is the board’s decision, and the board records that as a
            recusal.
          </p>

          <TextField
            label="A short title (optional)"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            testId="director-disclosure-title"
          />

          <Textarea
            label="The interest"
            value={interest}
            onChange={(event) => setInterest(event.target.value)}
            rows={4}
            testId="director-disclosure-interest"
          />

          <TextField
            label="The other party (optional)"
            value={counterparty}
            onChange={(event) => setCounterparty(event.target.value)}
            testId="director-disclosure-counterparty"
          />
          <FieldNote testId="director-disclosure-counterparty-note">
            The person or organization on the other side of it. Naming them is what lets whoever
            keeps your board’s records be shown this beside an agenda item or motion that mentions
            them, before the board votes. Where the interest is your own — your compensation, say —
            there is no other party to name.
          </FieldNote>

          <TextField
            label="Date disclosed"
            type="date"
            value={disclosedOn}
            onChange={(event) => setDisclosedOn(event.target.value)}
            testId="director-disclosure-date"
          />
          {hasDate ? null : (
            <FieldNote testId="director-disclosure-date-note">
              A disclosure is recorded with the date it was made.
            </FieldNote>
          )}

          {/* The words this act affirms, from `/me` (contract §8.3), above the act. */}
          {record.intent !== null ? (
            <p
              className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong"
              {...testIdProp('director-disclosure-intent')}
            >
              {record.intent.text}
            </p>
          ) : null}

          {/* CBG-FIX127: the pause's line (S1) before Save, until a press says it as the error. */}
          {record.pauseLine !== null && !record.paused ? (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
              {...testIdProp('director-disclosure-pause-line')}
            >
              {record.pauseLine}
            </p>
          ) : null}
          {shownError !== null ? (
            <ErrorMessage testId="director-disclosure-error">{shownError}</ErrorMessage>
          ) : null}

          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={record.pending}
              onClick={cancel}
              testId="director-disclosure-cancel"
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={record.working}
              disabled={!ready}
              onClick={begin}
              testId="director-disclosure-save"
            >
              Record it
            </Button>
          </div>

          {/* The sheet, nested in this dialog: the fields stay where they are behind it. */}
          {record.dialog}
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
